import { memo, useState, useRef, useMemo, useCallback } from 'react';
import { toDeg, normDeg, snapTo } from './SharedProtractor';
import { useQuizTheme } from './quizTheme';
import { aiguilleJuste } from '../../../utils/questionUtils';
import HorlogeAiguillesRenderer from '../renderers/HorlogeAiguillesRenderer';

/**
 * Horloge interactive : l'élève fait tourner les aiguilles pour indiquer
 * l'heure donnée dans l'énoncé (subType 'placer_aiguilles', gen_1032).
 *
 * - Glisser près de la pointe d'une aiguille → elle suit le doigt/la souris
 * - Grande aiguille et trotteuse : aimantées au multiple de 5 min (30°) au relâcher
 * - Petite aiguille : LIBRE — le savoir évalué est « entre deux chiffres »
 * - Valider → feedback par aiguille (règles de questionUtils.aiguilleJuste),
 *   2 essais, puis « Passer » (les aiguilles attendues s'affichent en fantôme)
 *
 * Le composant ne produit JAMAIS de verdict transmis : la réponse émise est
 * la string JSON des ANGLES posés { heures, minutes, secondes? } — le scoring
 * commun (compareAnswers → comparePlacerAiguilles) recalcule tout.
 *
 * Props (contrat de montage identique aux autres interactifs : selectedValue
 * lu UNE FOIS au montage, key d'identité fournie par la surface hôte) :
 * @param {boolean} avecTrotteuse - la consigne comporte des secondes
 * @param {string} bonneReponse - JSON { aiguilles: { h, min, sec } }
 * @param {string} selectedValue - JSON angles existants (restauration)
 * @param {Function} onAnswer - (jsonString) =>
 * @param {Function} onComplete - (bool) =>
 * @param {Function} onAbandon - présent en passation seulement
 * @param {Object|null} corrige - Correction sur place (lot 6, 2026-09-21), voir plus bas
 */

const CX = 120;
const CY = 120;
const BLEU = '#1E3A8A';
const ROUGE_TROTTEUSE = '#dc2626';

/** Angle depuis 12 h, sens horaire, du point (x, y) vu du centre. */
const angleDepuisMidi = (x, y) => normDeg(toDeg(Math.atan2(x - CX, CY - y)));

/** Pointe d'une aiguille d'angle donné, à distance r du centre. */
const pointe = (angleDeg, r) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: CX + r * Math.sin(rad), y: CY - r * Math.cos(rad) };
};

// géométrie des aiguilles : identique aux cadrans du gen_1031
const AIGUILLES = {
    heures: { points: '115.5,122 120,62 124.5,122 120,134', longueur: 58, fill: BLEU },
    minutes: { points: '117.2,122 120,32 122.8,122 120,134', longueur: 88, fill: BLEU },
    secondes: { points: '119,124 120,26 121,124 120,142', longueur: 94, fill: ROUGE_TROTTEUSE },
};

const NOMS_FR = { heures: 'la petite aiguille (heures)', minutes: 'la grande aiguille (minutes)', secondes: 'la trotteuse (secondes)' };

const HorlogeAiguillesEleve = memo(function HorlogeAiguillesEleve({
    avecTrotteuse = false,
    bonneReponse, selectedValue, onAnswer, onComplete, onAbandon, corrige = null,
}) {
    const clair = useQuizTheme() === 'clair';
    const svgRef = useRef(null);
    const CORRECTION = { juste: '#10b981', faux: '#ef4444' };

    const cible = useMemo(() => {
        try { return JSON.parse(bonneReponse || '{}').aiguilles || null; } catch { return null; }
    }, [bonneReponse]);

    const mains = useMemo(
        () => (avecTrotteuse ? ['heures', 'minutes', 'secondes'] : ['heures', 'minutes']),
        [avecTrotteuse],
    );

    // toutes les aiguilles partent sur 12 h ; restauration éventuelle
    const [angles, setAngles] = useState(() => {
        const base = { heures: 0, minutes: 0, ...(avecTrotteuse ? { secondes: 0 } : {}) };
        if (selectedValue) {
            try {
                const p = JSON.parse(selectedValue);
                for (const m of Object.keys(base)) {
                    if (typeof p[m] === 'number' && Number.isFinite(p[m])) base[m] = normDeg(p[m]);
                }
            } catch { /* réponse illisible : départ à 12 h */ }
        }
        return base;
    });
    const [bougees, setBougees] = useState(() => new Set(selectedValue ? mains : []));
    const [drag, setDrag] = useState(null);              // nom de l'aiguille saisie
    const [essais, setEssais] = useState(0);
    const [feedback, setFeedback] = useState(null);      // { type, msg, fausses: [] }
    const [termine, setTermine] = useState(false);       // validé ou passé
    const [montrerSolution, setMontrerSolution] = useState(false);
    const [confirmAbandon, setConfirmAbandon] = useState(false);

    // ─── Correction sur place (lot 6, 2026-09-21) ───
    // `corrige` n'arrive que si la réponse est fausse (contrat dans corrige.jsx).
    // L'horloge sait déjà se corriger (deux essais, aiguilles fausses en rouge,
    // « Passer » montre les aiguilles attendues en pointillé) ; la correction
    // fige et harmonise : aiguilles justes en vert, fausses en rouge (règle même
    // du scoring, aiguilleJuste, sur les angles que Quiz a jugés), boutons
    // masqués, et À CÔTÉ le cadran attendu (« La réponse »), rendu par le
    // composant statique qui existe déjà. Pas de pointillé superposé en
    // correction : trois aiguilles fausses et trois attendues sur un seul cadran
    // ne se lisaient plus (constat prof 2026-09-21). Sans `corrige`, le rendu
    // d'avant, « Passer » compris.
    const anglesJuges = useMemo(() => {
        if (!corrige) return null;
        try { return JSON.parse(corrige.reponseEleve || '{}') || {}; } catch { return {}; }
    }, [corrige]);
    const faussesCorrige = useMemo(
        () => (anglesJuges && cible ? mains.filter((m) => !aiguilleJuste(m, anglesJuges[m] ?? angles[m], cible)) : null),
        [anglesJuges, cible, mains, angles],
    );

    const emitAnswer = useCallback((a) => {
        const arrondi = {};
        for (const [k, v] of Object.entries(a)) arrondi[k] = Math.round(v * 10) / 10;
        onAnswer(JSON.stringify(arrondi));
    }, [onAnswer]);

    // ─── Drag ───

    const pointDansSvg = (e) => {
        const svg = svgRef.current;
        if (!svg) return null;
        const rect = svg.getBoundingClientRect();
        return {
            x: ((e.clientX - rect.left) / rect.width) * 240,
            y: ((e.clientY - rect.top) / rect.height) * 240,
        };
    };

    const handlePointerDown = (e) => {
        if (termine) return;
        const p = pointDansSvg(e);
        if (!p) return;
        // l'aiguille saisie = celle dont la POINTE est la plus proche
        let choisie = null;
        let dMin = Infinity;
        for (const m of mains) {
            const tip = pointe(angles[m], AIGUILLES[m].longueur);
            const d = Math.hypot(p.x - tip.x, p.y - tip.y);
            if (d < dMin) { dMin = d; choisie = m; }
        }
        if (dMin > 55) return;                           // trop loin de toute pointe
        e.preventDefault();
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* environnements sans pointer capture */ }
        setDrag(choisie);
        setFeedback(null);
        setAngles((prev) => ({ ...prev, [choisie]: angleDepuisMidi(p.x, p.y) }));
    };

    const handlePointerMove = (e) => {
        if (!drag) return;
        const p = pointDansSvg(e);
        if (!p) return;
        e.preventDefault();
        setAngles((prev) => ({ ...prev, [drag]: angleDepuisMidi(p.x, p.y) }));
    };

    const handlePointerUp = () => {
        if (!drag) return;
        // La position finale se calcule ICI, dans le gestionnaire, et c'est la
        // même valeur qu'on donne à React et au parent. JAMAIS dans la fonction
        // passée à setState : React est libre de la rejouer, et un rejeu qui
        // prévient le parent fait boucler jusqu'à « Maximum update depth »
        // (vécu le 2026-09-21, rechargement à chaud pendant un glisser).
        // `angles` est la dernière position dessinée : le relâchement est un
        // événement discret, traité une fois les mouvements appliqués.
        const suiv = { ...angles };
        // grande aiguille et trotteuse : aimantées au repère de 5 min (30°)
        if (drag !== 'heures') suiv[drag] = normDeg(snapTo(angles[drag], 30));
        setAngles(suiv);
        emitAnswer(suiv);
        setBougees((prev) => new Set(prev).add(drag));
        setDrag(null);
    };

    // ─── Valider / Passer / Abandonner ───

    const toutesBougees = mains.every((m) => bougees.has(m));

    const handleValider = () => {
        if (!cible || termine) return;
        const fausses = mains.filter((m) => !aiguilleJuste(m, angles[m], cible));
        if (fausses.length === 0) {
            setFeedback({ type: 'success', msg: 'Bien joué, l\'horloge est à l\'heure !', fausses: [] });
            setTermine(true);
            emitAnswer(angles);
            onComplete(true);
            return;
        }
        setEssais((n) => n + 1);
        const liste = fausses.map((m) => NOMS_FR[m]).join(' et ');
        setFeedback({
            type: 'error',
            msg: `Pas encore : ${liste} ${fausses.length > 1 ? 'ne sont pas bien placées' : 'n\'est pas bien placée'}.`,
            fausses,
        });
    };

    const handlePasser = () => {
        setMontrerSolution(true);
        setTermine(true);
        setFeedback({ type: 'skip', msg: 'Voici où devaient être les aiguilles (en vert).', fausses: [] });
        emitAnswer(angles);
        onComplete(true);
    };

    const handleAbandon = () => {
        onAnswer(JSON.stringify({}));
        onComplete(true);
        if (onAbandon) onAbandon();
    };

    // ─── Rendu ───

    const anglesCible = cible ? {
        heures: (cible.h % 12) * 30 + cible.min * 0.5,
        minutes: cible.min * 6,
        ...(cible.sec != null ? { secondes: cible.sec * 6 } : {}),
    } : null;

    const graduations = useMemo(() => {
        const items = [];
        for (let i = 0; i < 60; i++) {
            const maj = i % 5 === 0;
            const p1 = pointe(i * 6, maj ? 96 : 101);
            const p2 = pointe(i * 6, 106);
            items.push(
                <line
                    key={`g${i}`}
                    x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                    stroke={maj ? '#64748b' : '#94a3b8'}
                    strokeWidth={maj ? 2.5 : 1}
                />,
            );
        }
        for (let i = 1; i <= 12; i++) {
            const p = pointe(i * 30, 80);
            items.push(
                <text
                    key={`n${i}`} x={p.x} y={p.y + 6}
                    fontSize={17} fontWeight={700} fill={BLEU}
                    textAnchor="middle" fontFamily="system-ui, sans-serif"
                >
                    {i}
                </text>,
            );
        }
        return items;
    }, []);

    const instruction = (() => {
        if (corrige) return 'Tes aiguilles justes en vert, fausses en rouge. À côté : l\'heure attendue.';
        if (termine) return feedback?.msg || '';
        if (feedback?.type === 'error') return `${feedback.msg} Ajuste, puis revalide.`;
        if (drag) return `Tu déplaces ${NOMS_FR[drag]}.`;
        return 'Attrape chaque aiguille par sa pointe et fais-la tourner.';
    })();

    // Son cadran : seul, ou à côté du cadran attendu en correction
    const cadran = (
            <div className={`w-full max-w-[340px] mx-auto rounded-xl border-2 bg-white select-none ${termine || corrige ? (clair ? 'border-slate-300' : 'border-slate-600') : 'border-blue-500/50'}`}>
                <svg
                    ref={svgRef}
                    viewBox="0 0 240 240"
                    className="w-full h-auto block touch-none"
                    style={{ cursor: termine || corrige ? 'default' : (drag ? 'grabbing' : 'grab') }}
                    onPointerDown={corrige ? undefined : handlePointerDown}
                    onPointerMove={corrige ? undefined : handlePointerMove}
                    onPointerUp={corrige ? undefined : handlePointerUp}
                    onPointerCancel={corrige ? undefined : handlePointerUp}
                    role="img"
                    aria-label="Horloge interactive : fais tourner les aiguilles pour indiquer l'heure demandée."
                >
                    <circle cx={CX} cy={CY} r={112} fill="#eff6ff" stroke={BLEU} strokeWidth={4} />
                    {graduations}
                    {/* aiguilles attendues en fantôme (après « Passer » ; jamais en correction, le cadran attendu est à côté) */}
                    {montrerSolution && !corrige && anglesCible && mains.map((m) => (
                        <polygon
                            key={`sol-${m}`}
                            points={AIGUILLES[m].points}
                            fill="none"
                            stroke="#10b981"
                            strokeWidth={2}
                            strokeDasharray="5 4"
                            transform={`rotate(${anglesCible[m]} ${CX} ${CY})`}
                        />
                    ))}
                    {/* aiguilles de l'élève (la trotteuse au-dessus) */}
                    {mains.map((m) => (
                        <polygon
                            key={m}
                            points={AIGUILLES[m].points}
                            data-etat={faussesCorrige ? (faussesCorrige.includes(m) ? 'faux' : 'juste') : undefined}
                            fill={faussesCorrige ? CORRECTION[faussesCorrige.includes(m) ? 'faux' : 'juste'] : (feedback?.fausses?.includes(m) ? '#ef4444' : AIGUILLES[m].fill)}
                            opacity={drag && drag !== m ? 0.55 : 1}
                            transform={`rotate(${angles[m]} ${CX} ${CY})`}
                        />
                    ))}
                    <circle cx={CX} cy={CY} r={5.5} fill={BLEU} />
                </svg>
            </div>
    );

    return (
        <div className="space-y-3">
            {/* Barre du haut : état + abandon */}
            <div className="flex items-center justify-between text-xs sm:text-sm">
                <span className={clair ? 'text-slate-600' : 'text-slate-400'}>
                    🕐 Aiguilles à placer : {mains.map((m) => NOMS_FR[m].split(' (')[0]).join(', ')}
                </span>
                {onAbandon && !confirmAbandon && !termine && !corrige && (
                    <button
                        onClick={() => setConfirmAbandon(true)}
                        className={`px-2.5 py-1 text-xs rounded-md border transition ${clair ? 'text-slate-500 border-slate-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300' : 'text-slate-400 border-slate-600 hover:bg-red-900/30 hover:text-red-300 hover:border-red-700/60'}`}
                    >
                        🏳 Abandonner
                    </button>
                )}
            </div>

            {onAbandon && confirmAbandon && (
                <div className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs ${clair ? 'bg-red-50 border-red-300' : 'bg-red-900/20 border-red-700/50'}`}>
                    <span className={clair ? 'text-red-700' : 'text-red-200'}>
                        Abandonner ? Ton placement sera effacé et la question comptée fausse.
                    </span>
                    <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={handleAbandon} className="px-2.5 py-1 rounded-md bg-red-600 hover:bg-red-700 text-white font-semibold transition">
                            Oui, abandonner
                        </button>
                        <button onClick={() => setConfirmAbandon(false)} className={`px-2.5 py-1 rounded-md border transition ${clair ? 'text-slate-600 border-slate-300 hover:bg-slate-100' : 'text-slate-300 border-slate-600 hover:bg-slate-700'}`}>
                            Continuer
                        </button>
                    </div>
                </div>
            )}

            {/* Instruction */}
            <p className={`text-xs sm:text-sm italic min-h-[20px] ${feedback?.type === 'error' ? 'text-red-500 not-italic font-medium' : feedback?.type === 'success' ? 'text-emerald-500 not-italic font-medium' : (clair ? 'text-slate-500' : 'text-slate-400')}`}>
                {instruction}
            </p>

            {/* Le cadran, et en correction le cadran attendu à côté */}
            {corrige ? (
                <div className="grid gap-3 sm:grid-cols-2 sm:items-start" data-testid="figures-cote-a-cote">
                    {cadran}
                    <div className="w-full max-w-[340px] mx-auto">
                        <span className="block mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">La réponse</span>
                        <div className="rounded-xl border-2 border-dashed border-emerald-500 bg-white">
                            <HorlogeAiguillesRenderer reponse={corrige.bonneReponse} avecTrotteuse={avecTrotteuse} largeur="100%" />
                        </div>
                    </div>
                </div>
            ) : cadran}

            {/* Valider / Passer (jamais en correction) */}
            {!termine && !corrige && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={handleValider}
                        disabled={!toutesBougees}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
                            toutesBougees
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : (clair ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-700 text-slate-500 cursor-not-allowed')
                        }`}
                    >
                        ✓ Valider mon horloge
                    </button>
                    {essais >= 2 && (
                        <button
                            onClick={handlePasser}
                            className={`px-3 py-2 rounded-lg text-sm border transition ${clair ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200' : 'bg-slate-700/60 text-slate-200 border-slate-600 hover:bg-slate-600'}`}
                        >
                            ⏭ Passer
                        </button>
                    )}
                </div>
            )}
        </div>
    );
});

export default HorlogeAiguillesEleve;
